import React, { useEffect, useRef, useState } from "react";
import { DatePicker, Input, InputNumber, Select, Spin } from "antd";
import dayjs from "dayjs";
import "./inlineEdit.scss";

const { Option } = Select;

/**
 * ClickUp-style inline cell editing.
 *
 * A cell shows its normal read-only rendering until it is clicked, then swaps
 * the same box for an editor. Committing fires `onSave(next)` and the caller
 * updates the row optimistically, so the table reflects the new value without a
 * refetch. The affordance is deliberately colour-neutral (thin grey outline on
 * hover) so status pills, facility tags and the pink table chrome keep the exact
 * colours they have everywhere else on the platform.
 *
 * `onSave` may return a promise; while it is pending the cell shows a spinner
 * and stays locked. A rejected promise just closes the editor — reverting the
 * value is the caller's job, since only it owns the row data.
 */

// Shared shell: hover affordance, click-to-edit, pending spinner.
// `maxWidth` caps only the long free-text columns; everywhere else the cell is
// left unconstrained so the column grows to fit its full value.
const InlineShell = ({
  editing,
  saving,
  disabled,
  onActivate,
  title,
  children,
  maxWidth,
}) => {
  const style = maxWidth ? { maxWidth } : undefined;
  const clamp = maxWidth ? " inlineEdit--clamp" : "";

  if (editing || saving) {
    return (
      <div className={`inlineEdit inlineEdit--editing${clamp}`} style={style}>
        {children}
        {saving && (
          <span className="inlineEdit__spinner">
            <Spin size="small" />
          </span>
        )}
      </div>
    );
  }

  if (disabled) {
    return (
      <div className={`inlineEdit inlineEdit--readonly${clamp}`} style={style}>
        {children}
      </div>
    );
  }

  return (
    <div
      className={`inlineEdit${clamp}`}
      style={style}
      role="button"
      tabIndex={0}
      title={title || "Click to edit"}
      onClick={onActivate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onActivate();
        }
      }}
    >
      {children}
    </div>
  );
};

// Runs onSave, keeping the cell locked until it settles.
const useCommit = (onSave, close) => {
  const [saving, setSaving] = useState(false);
  const alive = useRef(true);
  useEffect(() => () => (alive.current = false), []);

  const commit = (next) => {
    const result = onSave?.(next);
    if (!result || typeof result.then !== "function") {
      close();
      return;
    }
    setSaving(true);
    result
      .catch(() => {})
      .finally(() => {
        if (!alive.current) return;
        setSaving(false);
        close();
      });
  };

  return [saving, commit];
};

/** Free text: Enter or blur commits, Escape cancels. */
export const InlineText = ({
  value,
  onSave,
  disabled,
  placeholder = "—",
  display,
  maxLength = 200,
  maxWidth,
  allowEmpty = false,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [saving, commit] = useCommit(onSave, () => setEditing(false));
  const escaped = useRef(false);

  const open = () => {
    setDraft(value ?? "");
    escaped.current = false;
    setEditing(true);
  };

  const finish = () => {
    const next = (draft ?? "").trim();
    // Clearing is only allowed where an empty value is meaningful — a site visit
    // must keep its name, but a note or a reason can legitimately be blanked.
    if (next === (value ?? "").trim() || (next === "" && !allowEmpty)) {
      setEditing(false);
      return;
    }
    commit(next);
  };

  return (
    <InlineShell
      editing={editing}
      saving={saving}
      disabled={disabled}
      onActivate={open}
      title={value || placeholder}
      maxWidth={maxWidth}
    >
      {editing || saving ? (
        <Input
          className="inlineEdit__input"
          size="small"
          autoFocus
          disabled={saving}
          maxLength={maxLength}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onPressEnter={finish}
          onBlur={() => {
            if (escaped.current) return;
            finish();
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              escaped.current = true;
              setEditing(false);
            }
          }}
        />
      ) : (
        display ?? <span className="styledDataIndex3">{value || placeholder}</span>
      )}
    </InlineShell>
  );
};

/**
 * Whole numbers — targets, limits, percentages. Enter or blur commits, Escape
 * cancels. `suffix` is display-only ("%"), so the value saved stays numeric.
 */
export const InlineNumber = ({
  value,
  onSave,
  disabled,
  placeholder = "—",
  min = 0,
  max,
  suffix = "",
  width = 90,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? null);
  const [saving, commit] = useCommit(onSave, () => setEditing(false));
  const escaped = useRef(false);

  const open = () => {
    setDraft(value ?? null);
    escaped.current = false;
    setEditing(true);
  };

  const finish = () => {
    // A blank box means "no value", which for a count is 0 rather than null —
    // the API columns behind these cells are all non-nullable numbers.
    const next = draft === null || draft === undefined || draft === "" ? 0 : Number(draft);
    if (Number.isNaN(next) || next === (value ?? 0)) {
      setEditing(false);
      return;
    }
    commit(next);
  };

  return (
    <InlineShell
      editing={editing}
      saving={saving}
      disabled={disabled}
      onActivate={open}
    >
      {editing || saving ? (
        <InputNumber
          className="inlineEdit__number"
          size="small"
          autoFocus
          disabled={saving}
          min={min}
          max={max}
          precision={0}
          style={{ width }}
          value={draft}
          onChange={setDraft}
          onPressEnter={finish}
          onBlur={() => {
            if (escaped.current) return;
            finish();
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              escaped.current = true;
              setEditing(false);
            }
          }}
        />
      ) : (
        <span className="styledDataIndex3">
          {value === null || value === undefined
            ? placeholder
            : `${value}${suffix}`}
        </span>
      )}
    </InlineShell>
  );
};

/**
 * Dropdown. Opens on the first click, so picking a value is click → click.
 * `options` is `[{ value, label, display }]`; `display` lets an option keep its
 * pill/tag rendering inside the list.
 */
export const InlineSelect = ({
  value,
  options = [],
  onSave,
  disabled,
  loading,
  display,
  placeholder = "—",
  allowClear = false,
  dropdownWidth = 220,
  maxWidth,
  minWidth,
}) => {
  const [editing, setEditing] = useState(false);
  const [saving, commit] = useCommit(onSave, () => setEditing(false));

  const handleChange = (next) => {
    if (next === value) {
      setEditing(false);
      return;
    }
    commit(next ?? null);
  };

  return (
    <InlineShell
      editing={editing}
      saving={saving}
      disabled={disabled}
      onActivate={() => setEditing(true)}
      maxWidth={maxWidth}
    >
      {editing || saving ? (
        <Select
          className="inlineEdit__select"
          style={minWidth ? { minWidth } : undefined}
          size="small"
          autoFocus
          defaultOpen
          showSearch
          allowClear={allowClear}
          disabled={saving}
          loading={loading}
          value={value ?? undefined}
          popupClassName="inlineEdit__dropdown"
          popupMatchSelectWidth={dropdownWidth}
          optionFilterProp="title"
          filterOption={(input, option) =>
            (option?.title || "").toLowerCase().includes(input.toLowerCase())
          }
          onChange={handleChange}
          onBlur={() => !saving && setEditing(false)}
          onKeyDown={(e) => e.key === "Escape" && setEditing(false)}
        >
          {options.map((opt) => (
            <Option key={String(opt.value)} value={opt.value} title={opt.label}>
              {opt.display ?? opt.label}
            </Option>
          ))}
        </Select>
      ) : (
        display ?? <span className="styledDataIndex3">{placeholder}</span>
      )}
    </InlineShell>
  );
};

/** Date picker, opened on the first click. Picking a day commits. */
export const InlineDate = ({
  value,
  onSave,
  disabled,
  display,
  placeholder = "—",
  format = "DD MMM YYYY",
}) => {
  const [editing, setEditing] = useState(false);
  const [saving, commit] = useCommit(onSave, () => setEditing(false));
  const current = value ? dayjs(value) : null;

  const handleChange = (next) => {
    if (!next) {
      setEditing(false);
      return;
    }
    if (current && next.isSame(current, "day")) {
      setEditing(false);
      return;
    }
    commit(next);
  };

  return (
    <InlineShell
      editing={editing}
      saving={saving}
      disabled={disabled}
      onActivate={() => setEditing(true)}
    >
      {editing || saving ? (
        <DatePicker
          className="inlineEdit__date"
          size="small"
          autoFocus
          open={!saving}
          disabled={saving}
          allowClear={false}
          format={format}
          value={current}
          onChange={handleChange}
          onOpenChange={(isOpen) => !isOpen && !saving && setEditing(false)}
        />
      ) : (
        display ?? (
          <span className="styledDataIndex3">
            {current?.isValid() ? current.format(format) : placeholder}
          </span>
        )
      )}
    </InlineShell>
  );
};

export default InlineShell;
